import React from 'react';
import { Tag, TrendingUp, Users, Landmark, Package, Wallet, Flame, Building2 } from 'lucide-react';
import { formatInr, formatSignedInr, formatSignedPercent } from '../../../utils/gmpBoard';

const valueTone = (value) => {
  if (value > 0) return 'text-emerald-600';
  if (value < 0) return 'text-rose-600';
  return 'text-slate-900';
};

export const CompanyKeyFigures = ({ row }) => {
  const minInvestment = row.price !== null && row.lotSize !== null ? row.price * row.lotSize : null;

  let subscriptionNote = 'Not reported by the source';
  if (row.subscription !== null) subscriptionNote = 'Overall times subscribed';
  else if (row.status === 'upcoming') subscriptionNote = 'Bidding has not started';

  const figures = [
    {
      label: 'Upper Price Band',
      icon: Tag,
      value: row.price !== null ? formatInr(row.price) : 'TBA',
      note: row.price !== null ? 'Per share, cut-off price' : 'Not announced yet',
    },
    {
      label: 'Expected Gain / Share',
      icon: TrendingUp,
      value: row.hasGmp ? formatSignedInr(row.gmp) : '—',
      tone: row.hasGmp ? valueTone(row.gmp) : undefined,
      note:
        row.hasGmp && row.gmpPercent !== null
          ? `${formatSignedPercent(row.gmpPercent)} over issue price`
          : 'Awaiting a grey market quote',
    },
    {
      label: 'Subscription',
      icon: Users,
      value: row.subscription !== null ? `${row.subscription.toFixed(2)}x` : '—',
      note: subscriptionNote,
    },
    {
      label: 'Issue Size',
      icon: Landmark,
      value: row.sizeCr !== null ? `${formatInr(row.sizeCr, 2)} Cr` : '—',
      note: 'Total offer size',
    },
    {
      label: 'Lot Size',
      icon: Package,
      value: row.lotSize !== null ? `${row.lotSize.toLocaleString('en-IN')} shares` : '—',
      note: 'Minimum bid quantity',
    },
    {
      label: 'Investment / Lot',
      icon: Wallet,
      value: minInvestment !== null ? formatInr(minInvestment) : '—',
      note: 'One lot at the upper band',
    },
    {
      label: 'Fire Rating',
      icon: Flame,
      value: `${row.fireRating}/5`,
      note: row.sentiment,
    },
    {
      label: 'Listing Board',
      icon: Building2,
      value: row.board,
      note: row.exchange ? `${row.exchange} platform` : 'Exchange not specified',
    },
  ];

  return (
    <section aria-labelledby="gmp-key-figures" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm">
      <h2 id="gmp-key-figures" className="px-4 sm:px-6 pt-4 sm:pt-5 text-base font-extrabold text-slate-900">
        Key Figures
      </h2>
      <dl className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-px bg-slate-100 mt-4 border-t border-slate-100 rounded-b-2xl overflow-hidden">
        {figures.map(({ label, icon: Icon, value, note, tone }) => (
          <div key={label} className="bg-white p-4 sm:p-5">
            <dt className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              <Icon className="w-3.5 h-3.5 text-[#008080] flex-shrink-0" />
              {label}
            </dt>
            <dd className={`mt-1.5 text-lg 2xl:text-xl font-extrabold tracking-tight ${tone || 'text-slate-900'}`}>
              {value}
            </dd>
            <dd className="text-[11px] text-slate-500 mt-0.5">{note}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default CompanyKeyFigures;
